/**
 * Super-admin ledger integrity: BS vs PL mismatch counts per pump, with daily drill-down.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import { fetchLedgerMismatchDetail, fetchLedgerMismatches, friendlyFetchError } from './adminApi';
import { formatReportDate } from '../../../utils/reportFormat';
import './admin-dashboard.css';

function formatAmount(value) {
  const n = Number(value || 0);
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(value) {
  return value || '—';
}

const AdminLedgerErrors = (props) => {
  const history = useHistory();
  const { pumpId } = useParams();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (profile) {
          setReady(true);
        } else {
          history.goBack();
        }
      })
      .catch(() => history.goBack());
  }, [history]);

  if (!ready) {
    return (
      <div className="main-container page-wrap admin-platform">
        <div className="d-md-flex">
          <Sidebar_Progress props={props} progress={34} />
          <div className="content-area">
            <LoginHeaderOperational props={props} page_title="Ledger errors" />
            <div className="container pb-3 mt-3">
              <p className="text-muted">Checking access…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return pumpId
    ? <LedgerErrorDetail props={props} pumpId={pumpId} />
    : <LedgerErrorList props={props} />;
};

const LedgerErrorList = ({ props }) => {
  const [pumps, setPumps] = useState([]);
  const [meta, setMeta] = useState({ pumps_with_errors: 0, total_errors: 0, checked_pumps: 0, generated_at: '' });
  const [onlyErrors, setOnlyErrors] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  function load(refresh = false, errorsOnly = onlyErrors) {
    setLoading(true);
    setError('');
    fetchLedgerMismatches({ onlyErrors: errorsOnly, refresh })
      .then((data) => {
        setPumps(data.pumps || []);
        setMeta({
          pumps_with_errors: data.pumps_with_errors || 0,
          total_errors: data.total_errors || 0,
          checked_pumps: data.checked_pumps || 0,
          generated_at: data.generated_at || '',
        });
      })
      .catch((err) => setError(friendlyFetchError(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load(false, onlyErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyErrors]);

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={34} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Ledger errors" />
          <div className="container pb-3 mt-3">
            <Link to="/admin_dashboard" className="small">← Control Center</Link>

            <header className="admin-dash-home__header mt-3">
              <div>
                <h2 className="admin-dash-home__title">Ledger calculation errors</h2>
                <p className="admin-dash-home__subtitle">
                  Balance sheet net vs profit &amp; loss net (cylinder and opening-balance rows excluded).
                  Click an error number to see the dates that diverged.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => load(true)}
                disabled={loading}
              >
                {loading ? 'Scanning…' : 'Refresh scan'}
              </button>
            </header>

            <div className="admin-dash-kpi-row mb-3">
              <div className="admin-dash-kpi-card admin-dash-kpi-card--danger">
                <p className="admin-dash-kpi-card__value">{meta.pumps_with_errors}</p>
                <p className="admin-dash-kpi-card__label">Pumps with errors</p>
              </div>
              <div className="admin-dash-kpi-card admin-dash-kpi-card--warning">
                <p className="admin-dash-kpi-card__value">{meta.total_errors}</p>
                <p className="admin-dash-kpi-card__label">Mismatch days</p>
              </div>
              <div className="admin-dash-kpi-card admin-dash-kpi-card--default">
                <p className="admin-dash-kpi-card__value">{meta.checked_pumps}</p>
                <p className="admin-dash-kpi-card__label">Pumps checked</p>
              </div>
            </div>

            <div className="admin-panel admin-dash-panel">
              <div className="admin-dash-panel__head">
                <h5 className="mb-0">Pumps</h5>
                <label className="small mb-0">
                  <input
                    type="checkbox"
                    className="me-1"
                    checked={onlyErrors}
                    onChange={(e) => setOnlyErrors(e.target.checked)}
                  />
                  Show only pumps with errors
                </label>
              </div>
              {meta.generated_at ? (
                <p className="admin-dash-panel__meta">Last scan {meta.generated_at}</p>
              ) : null}

              {error ? <p className="text-danger">{error}</p> : null}
              {loading ? <p className="text-muted">Scanning ledgers across pumps… this can take a minute.</p> : null}

              {!loading && pumps.length === 0 ? (
                <div className="admin-dash-empty">
                  <p className="mb-0 text-muted">
                    {onlyErrors ? 'No ledger mismatches found.' : 'No pumps to list.'}
                  </p>
                </div>
              ) : null}

              {!loading && pumps.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover admin-pump-table admin-dash-pump-table">
                    <thead>
                      <tr>
                        <th>Pump</th>
                        <th>Check window</th>
                        <th>BS net</th>
                        <th>PL net</th>
                        <th>Difference</th>
                        <th>First mismatch</th>
                        <th>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pumps.map((pump) => (
                        <tr key={pump.pump_id}>
                          <td>
                            <div className="admin-dash-pump-name">{pump.pump_name}</div>
                            <div className="admin-dash-pump-meta">
                              #{pump.pump_id} · {pump.pump_phone || pump.pump_email || '—'}
                            </div>
                          </td>
                          <td className="small">
                            {formatDate(pump.check_from)} → {formatDate(pump.check_to)}
                          </td>
                          <td>{formatAmount(pump.bs_net)}</td>
                          <td>{formatAmount(pump.pl_net)}</td>
                          <td>{formatAmount(pump.difference)}</td>
                          <td>{formatDate(pump.first_mismatch_date)}</td>
                          <td>
                            <ErrorCountLink pumpId={pump.pump_id} count={pump.error_count} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LedgerErrorDetail = ({ props, pumpId }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchLedgerMismatchDetail(pumpId)
      .then((data) => setDetail(data))
      .catch((err) => setError(friendlyFetchError(err)))
      .finally(() => setLoading(false));
  }, [pumpId]);

  const mismatchDays = detail?.daily_mismatches || [];
  const errorDays = useMemo(
    () => mismatchDays.filter((row) => Math.abs(Number(row.day_difference || 0)) >= 0.01),
    [mismatchDays]
  );

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={34} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Ledger error detail" />
          <div className="container pb-3 mt-3">
            <Link to="/admin-ledger-errors" className="small">← All ledger errors</Link>

            {loading ? <p className="mt-3 text-muted">Loading mismatch details…</p> : null}
            {error ? <p className="mt-3 text-danger">{error}</p> : null}

            {detail && !loading ? (
              <>
                <header className="admin-dash-home__header mt-3">
                  <div>
                    <h2 className="admin-dash-home__title">{detail.pump_name || `Pump #${pumpId}`}</h2>
                    <p className="admin-dash-home__subtitle">
                      #{detail.pump_id} · {detail.pump_phone || '—'} · {detail.pump_email || '—'}
                      {detail.confg_date ? ` · config ${detail.confg_date}` : ''}
                    </p>
                  </div>
                  <ErrorCountLink pumpId={detail.pump_id} count={detail.error_count} asSpan />
                </header>

                <div className="admin-panel admin-dash-panel mb-3">
                  <h5>What was compared</h5>
                  <p className="admin-dash-panel__meta">
                    Window {formatDate(detail.check_from)} → {formatDate(detail.check_to)}.
                    Excluded types: {(detail.excluded_ac_types || []).join(', ') || 'none'}.
                  </p>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th />
                          <th>Dr</th>
                          <th>Cr</th>
                          <th>Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Balance sheet</td>
                          <td>{formatAmount(detail.bs_dr)}</td>
                          <td>{formatAmount(detail.bs_cr)}</td>
                          <td>{formatAmount(detail.bs_net)}</td>
                        </tr>
                        <tr>
                          <td>Profit &amp; loss</td>
                          <td>{formatAmount(detail.pl_dr)}</td>
                          <td>{formatAmount(detail.pl_cr)}</td>
                          <td>{formatAmount(detail.pl_net)}</td>
                        </tr>
                        <tr>
                          <td><strong>Difference (BS − PL)</strong></td>
                          <td colSpan={2} />
                          <td><strong>{formatAmount(detail.difference)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="small mb-0">
                    {detail.balanced
                      ? 'Books match for this window.'
                      : `First divergence on ${formatDate(detail.first_mismatch_date)}. ${detail.error_count} day(s) posted a BS vs PL gap.`}
                  </p>
                  <Link to={`/admin-pump/${detail.pump_id}`} className="btn btn-sm btn-outline-primary mt-3">
                    Open pump accounting tools
                  </Link>
                </div>

                <div className="admin-panel admin-dash-panel">
                  <h5>Mismatch days ({errorDays.length})</h5>
                  <p className="admin-dash-panel__meta">
                    Days where that day’s balance-sheet movement did not equal P&amp;L movement.
                    Running difference shows how the gap accumulated.
                  </p>
                  {mismatchDays.length === 0 ? (
                    <p className="text-muted mb-0">No daily gaps in this window.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Day BS</th>
                            <th>Day PL</th>
                            <th>Day difference</th>
                            <th>Running BS</th>
                            <th>Running PL</th>
                            <th>Running difference</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mismatchDays.map((row) => {
                            const isErrorDay = Math.abs(Number(row.day_difference || 0)) >= 0.01;
                            return (
                              <tr key={row.ac_date} className={isErrorDay ? 'admin-ledger-error-row' : ''}>
                                <td>{formatReportDate(row.ac_date)}</td>
                                <td>{formatAmount(row.day_bs)}</td>
                                <td>{formatAmount(row.day_pl)}</td>
                                <td>{formatAmount(row.day_difference)}</td>
                                <td>{formatAmount(row.running_bs)}</td>
                                <td>{formatAmount(row.running_pl)}</td>
                                <td>{formatAmount(row.running_difference)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export function ErrorCountLink({ pumpId, count = 0, asSpan = false }) {
  const n = Number(count) || 0;
  const className = `admin-dash-error-count ${n > 0 ? 'admin-dash-error-count--bad' : 'admin-dash-error-count--ok'}`;
  if (asSpan) {
    return <span className={className}>{n}</span>;
  }
  return (
    <Link to={`/admin-ledger-errors/${pumpId}`} className={className} title="View ledger mismatch details">
      {n}
    </Link>
  );
}

export default AdminLedgerErrors;
